import React from 'react';

import { Card, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CodeIcon from '@material-ui/icons/Code';

import { ArgoCDLogoLarge } from './ArgoCDLogo';
import { BlackduckLogo } from './BlackduckLogo';
import { HCPVaultLogo } from './HCPVaultLogo';

const GILROY = '"Gilroy", ui-sans-serif, system-ui, sans-serif';
const PROXIMA = '"Proxima Nova", ui-sans-serif, system-ui, sans-serif';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';

  return {
    wrapperCard: {
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      background: theme.palette.background.paper,
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
    },
    titleGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    headerIcon: {
      color: theme.palette.text.secondary,
      fontSize: '1.25rem',
    },
    title: {
      fontFamily: GILROY,
      fontSize: '1rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    itemCount: {
      fontFamily: PROXIMA,
      fontSize: '0.75rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
      border: `1px solid ${theme.palette.divider}`,
      padding: '2px 10px',
      borderRadius: 10,
    },
    toolsGrid: {
      padding: theme.spacing(1.5, 1),
    },
    toolItem: {
      position: 'relative' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(1.25, 1),
      borderRadius: 10,
      cursor: 'pointer',
      border: `1px solid transparent`,
      transition: 'all 0.15s ease',

      '&:hover': {
        background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        border: `1px solid ${theme.palette.divider}`,
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 4px 12px rgba(0,0,0,0.3)'
          : '0 4px 12px rgba(0,0,0,0.07)',
      },

      '&:hover $externalBadge': {
        opacity: 1,
      },
      '&:hover $iconWrap svg': {
        opacity: 0.7,
      },
      '&:hover $label': {
        color: theme.palette.text.primary,
      },
    },
    iconWrap: {
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: '100%',
        height: '100%',
        transition: 'opacity 0.15s ease',
      },
    },
    label: {
      fontFamily: PROXIMA,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      textAlign: 'center' as const,
      lineHeight: 1.3,
      transition: 'color 0.15s ease',
    },
    externalBadge: {
      position: 'absolute' as const,
      top: 6,
      right: 6,
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: theme.palette.primary.main,
      opacity: 0,
      transition: 'opacity 0.15s ease',
    },
  };
});

const toolkitLinks = [
  {
    url: '/secrets-sharing',
    label: 'Share Secrets',
    icon: <HCPVaultLogo />,
    external: false,
  },
  {
    url: '/create/templates/default/dx-cicd-platform-hcpv-create-namespace',
    label: 'HCP Vault - Create Namespace',
    icon: <HCPVaultLogo />,
    external: false,
  },
  {
    url: '/create/templates/default/daas-project',
    label: 'Deployment as a Service - Create Project',
    icon: <ArgoCDLogoLarge />,
    external: false,
  },
  {
    url: '/create/templates/default/daas-application',
    label: 'Deployment as a Service - Create Application',
    icon: <ArgoCDLogoLarge />,
    external: false,
  },
  {
    url: '/create/templates/default/daas-application-set',
    label: 'Deployment as a Service - Create ApplicationSet',
    icon: <ArgoCDLogoLarge />,
    external: false,
  },
  {
    url: '/create/templates/default/blackduck-onboarding',
    label: 'Blackduck - Onboard Repository',
    icon: <BlackduckLogo />,
    external: false,
  },
];

export const HomeToolKit = () => {
  const classes = useStyles();

  return (
    <Card className={classes.wrapperCard}>
      <div className={classes.header}>
        <div className={classes.titleGroup}>
          <CodeIcon className={classes.headerIcon} />
          <Typography className={classes.title}>Engineering Toolkit</Typography>
        </div>
        <span className={classes.itemCount}>{toolkitLinks.length} tools</span>
      </div>

      <Grid container spacing={0} className={classes.toolsGrid}>
        {toolkitLinks.map(tool => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={tool.label}>
            <div
              className={classes.toolItem}
              onClick={() =>
                window.open(tool.url, tool.external ? '_blank' : '_self')
              }
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  window.open(tool.url, tool.external ? '_blank' : '_self');
                }
              }}
            >
              {tool.external && <span className={classes.externalBadge} />}
              <div className={classes.iconWrap}>{tool.icon}</div>
              <Typography className={classes.label}>{tool.label}</Typography>
            </div>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};
